import { type QuantaHeaders, sha256Hex } from './auth';
import { quantaApi } from './quantaApi';
import { sameQuantaIdentity } from './quantaIdentity';
import { type MigrationAuthority, type MigrationWallet, validateWalletApprovalContext } from './walletMigration';

export interface VaultLoginContext {
  operation: 'ducat:quanta:vault-login:v1';
  origin: string;
  userId: string;
  revision: number;
  network: MigrationWallet['network'];
  source: MigrationWallet;
  target: MigrationWallet;
  nonce: string;
  expiresAt: string;
}
export interface VaultLoginChallenge {
  proofVersion: 1;
  nonce: string;
  expiresAt: string;
  context: VaultLoginContext;
  message: string;
  hash: string;
}
export interface VaultLoginResult {
  status: 'completed' | 'already-linked' | 'pending' | 'expired';
  userId: string;
  revision?: number;
  nonce?: string;
  authority?: MigrationWallet;
  loginAlias?: MigrationWallet;
  alias?: MigrationWallet;
}
export interface VaultLoginRecovery {
  nonce: string;
  userId: string;
  source: MigrationWallet;
  target: MigrationWallet;
  expiresAt: string;
}
export function vaultLoginMessage(context: VaultLoginContext): string {
  if (context.operation !== 'ducat:quanta:vault-login:v1') throw new Error('VAULT_LOGIN_INVALID_CHALLENGE');
  // Both operation types use the same bounded, single-line identity/scalar fields.
  validateWalletApprovalContext(context);
  const message = [
    'Ducat Quanta vault sign-in',
    'Allow target to sign in. Account authority and points stay. No funds/vault transfer. Not login.',
    `Operation: ${context.operation}`,
    `Origin: ${context.origin}`,
    `Account: ${context.userId}`,
    `Revision: ${context.revision}`,
    `Network: ${context.network}`,
    `Source: ${context.source.address}`,
    `Source key: ${context.source.publicKey}`,
    `Target: ${context.target.address}`,
    `Target key: ${context.target.publicKey}`,
    `Nonce: ${context.nonce}`,
    `Expires: ${context.expiresAt}`,
  ].join('\n');
  if (new TextEncoder().encode(message).byteLength > 800) throw new Error('VAULT_LOGIN_MESSAGE_TOO_LONG');
  return message;
}
export interface VaultLoginAuthority {
  userId: string;
  revision: number;
  authority: MigrationWallet;
  accountAuthority: MigrationWallet;
  canBind: boolean;
  migrated: boolean;
}
export async function fetchVaultLoginAuthority(headers: QuantaHeaders): Promise<VaultLoginAuthority> {
  const response = await quantaApi.get('/vault-login/authority', { headers });
  return response.data.data;
}
export async function fetchVaultLoginCapabilities() {
  const response = await quantaApi.get('/vault-login/capabilities');
  return response.data.data as { enabled: boolean; proofVersion: number };
}
export async function fetchVaultLoginTarget(target: MigrationWallet, headers: QuantaHeaders) {
  const response = await quantaApi.post('/vault-login/target', { target }, { headers });
  return response.data.data as { relationship: 'same' | 'other' | 'none' };
}
export function assertVaultLoginResult(result: VaultLoginResult, saved: Pick<VaultLoginRecovery, 'userId' | 'source' | 'target'>) {
  const alias = result.loginAlias ?? result.alias;
  if (
    !['completed', 'already-linked'].includes(result.status) ||
    result.userId !== saved.userId ||
    !result.authority ||
    !sameQuantaIdentity(result.authority, saved.source) ||
    result.authority.network !== saved.source.network ||
    !alias ||
    !sameQuantaIdentity(alias, saved.target) ||
    alias.network !== saved.target.network
  )
    throw new Error('VAULT_LOGIN_RESULT_MISMATCH');
}
export async function requestVaultLoginChallenge(
  authority: Pick<MigrationAuthority, 'userId' | 'revision' | 'authority'>,
  target: MigrationWallet,
  headers: QuantaHeaders,
) {
  const origin = window.location.origin;
  const response = await quantaApi.post('/vault-login/challenge', { origin, target }, { headers });
  const challenge = response.data.data as VaultLoginChallenge & VaultLoginResult;
  if (challenge.status === 'already-linked') {
    assertVaultLoginResult(challenge, { userId: authority.userId, source: authority.authority, target });
    return challenge;
  }
  if (
    challenge?.proofVersion !== 1 ||
    !/^[a-f0-9]{64}$/.test(challenge.nonce) ||
    !Number.isFinite(Date.parse(challenge.expiresAt)) ||
    Date.parse(challenge.expiresAt) <= Date.now() ||
    Date.parse(challenge.expiresAt) > Date.now() + 600_000
  )
    throw new Error('VAULT_LOGIN_INVALID_CHALLENGE');
  const expected: VaultLoginContext = {
    operation: 'ducat:quanta:vault-login:v1',
    origin,
    userId: authority.userId,
    revision: authority.revision,
    network: authority.authority.network,
    source: authority.authority,
    target,
    nonce: challenge.nonce,
    expiresAt: challenge.expiresAt,
  };
  const message = vaultLoginMessage(expected);
  if (
    JSON.stringify(challenge.context) !== JSON.stringify(expected) ||
    challenge.message !== message ||
    challenge.hash !== (await sha256Hex(message))
  )
    throw new Error('VAULT_LOGIN_INVALID_CHALLENGE');
  return challenge;
}
export async function confirmVaultLogin(
  challenge: VaultLoginChallenge,
  sourceSignature: string,
  targetSignature: string,
  headers: QuantaHeaders,
) {
  if (Date.parse(challenge.expiresAt) <= Date.now()) throw new Error('VAULT_LOGIN_EXPIRED');
  if (!sourceSignature || !targetSignature) throw new Error('VAULT_LOGIN_SIGNATURE_REQUIRED');
  const response = await quantaApi.post(
    '/vault-login/confirm',
    {
      origin: challenge.context.origin,
      nonce: challenge.nonce,
      sourceSignature,
      targetSignature,
    },
    { headers },
  );
  return response.data.data as VaultLoginResult;
}
export async function fetchVaultLoginStatus(nonce: string, headers: QuantaHeaders) {
  const response = await quantaApi.post('/vault-login/status', { nonce }, { headers });
  return response.data.data as VaultLoginResult;
}
const recoveryKey = 'quanta-vault-login-v1';
export function saveVaultLoginRecovery(value: VaultLoginRecovery | null) {
  try {
    if (value) window.sessionStorage.setItem(recoveryKey, JSON.stringify(value));
    else window.sessionStorage.removeItem(recoveryKey);
  } catch {
    /* The active flow remains usable with browser storage disabled. */
  }
}
export function readVaultLoginRecovery(): VaultLoginRecovery | null {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(recoveryKey) ?? 'null') as VaultLoginRecovery | null;
    if (
      value &&
      /^[a-f0-9]{64}$/.test(value.nonce) &&
      typeof value.userId === 'string' &&
      Number.isFinite(Date.parse(value.expiresAt)) &&
      [value.source, value.target].every(
        (identity) =>
          identity &&
          typeof identity.address === 'string' &&
          typeof identity.publicKey === 'string' &&
          ['testnet', 'mainnet'].includes(identity.network),
      )
    )
      return value;
  } catch {
    /* A public recovery handle grants no authentication. */
  }
  return null;
}
